import rewind from '@turf/rewind';
import inside from 'point-in-polygon-hao';
import robustPredicates from 'robust-predicates';

var Edge = function Edge(p1, p2, edgeType, index, contourId) {
  this.p1 = p1;
  this.p2 = p2;
  this.edgeType = edgeType;
  this.originalIndex = index;

  this.polygonContourId = contourId;
  this.interiorRing = false;

  this.minX = Math.min(p1.p[0], p2.p[0]);
  this.minY = Math.min(p1.p[1], p2.p[1]);

  this.maxX = Math.max(p1.p[0], p2.p[0]);
  this.maxY = Math.max(p1.p[1], p2.p[1]);

  this.intersectionPoints = [];
  this.nextEdge = null;
};

var Point = function Point(p) {
  this.p = p;
};

var Contour = function Contour(contourId, coords) {
  this.id = contourId;
  this.rawCoords = coords;
};

function fillQueue(polygon, line, polyEdges, lineEdges, polylineBbox) {
  var numberOfRingsInPolygon = 0;
  var contours = [];

  var linegeom = line.type === 'Feature' ? line.geometry : line;
  var linecoords = linegeom.type === 'LineString' ? [linegeom.coordinates] : linegeom.coordinates;

  var edgeCount = 0;

  for (var i = 0; i < linecoords.length; i++) {

    var lineLength = linecoords[i].length - 1;
    var p1 = new Point(linecoords[i][0]);
    var p2 = null;
    var prevEdge = {nextEdge: null};

    for (var ii = 0; ii < lineLength; ii++) {
      p2 = new Point(linecoords[i][ii + 1]);
      p1.nextPoint = p2;
      p2.prevPoint = p1;
      var e = new Edge(p1, p2, 'polyline', edgeCount, null);
      lineEdges.push(e);
      prevEdge.nextEdge = e;
      e.prevEdge = prevEdge;
      polylineBbox[0] = Math.min(polylineBbox[0], p1.p[0]);
      polylineBbox[1] = Math.min(polylineBbox[1], p1.p[1]);
      polylineBbox[2] = Math.max(polylineBbox[2], p1.p[0]);
      polylineBbox[3] = Math.max(polylineBbox[3], p1.p[1]);

      p1 = p2;
      edgeCount = edgeCount + 1;
      prevEdge = e;
    }
    polylineBbox[0] = Math.min(polylineBbox[0], linecoords[i][lineLength][0]);
    polylineBbox[1] = Math.min(polylineBbox[1], linecoords[i][lineLength][1]);
    polylineBbox[2] = Math.max(polylineBbox[2], linecoords[i][lineLength][0]);
    polylineBbox[3] = Math.max(polylineBbox[3], linecoords[i][lineLength][1]);
  }

  var polygeom = polygon.type === 'Feature' ? polygon.geometry : polygon;
  var polycoords = polygeom.type === 'Polygon' ? [polygeom.coordinates] : polygeom.coordinates;

  var polyLength = polycoords.length;

  for (var i$1 = 0; i$1 < polyLength; i$1++) {

    var polyLenth2 = polycoords[i$1].length;

    for (var ii$1 = 0; ii$1 < polyLenth2; ii$1++) {
      numberOfRingsInPolygon = numberOfRingsInPolygon + 1;

      var polygonSet = polycoords[i$1][ii$1];
      var polyLenth3 = polygonSet.length;
      
      contours.push(new Contour(numberOfRingsInPolygon, polygonSet));

      var firstPoint = new Point(polygonSet[0]);
      var p1$1 = firstPoint;
      var p2$1 = (void 0), e$1 = null;
      var prevEdge$1 = {nextEdge: null, prevEdge: null};
      var firstEdge = null;

      for (var iii = 1; iii < polyLenth3; iii++) {
        p2$1 = new Point(polygonSet[iii]);
        p1$1.nextPoint = p2$1;
        p2$1.prevPoint = p1$1;

        e$1 = new Edge(p1$1, p2$1, 'polygon', edgeCount, numberOfRingsInPolygon);
        prevEdge$1.nextEdge = e$1;
        e$1.prevEdge = prevEdge$1;
        if (iii === 1) { firstEdge = e$1; }

        if (ii$1 > 0) { e$1.interiorRing = true; }
        e$1.intersectPolylineBbox = edgeIntersectsBbox(e$1, polylineBbox);
        polyEdges.push(e$1);

        p1$1 = p2$1;
        edgeCount = edgeCount + 1;
        prevEdge$1 = e$1;
      }

      e$1.nextEdge = firstEdge;
      firstEdge.prevEdge = e$1;
      p2$1.nextPoint = firstPoint.nextPoint;
      firstPoint.prevPoint = p2$1.prevPoint;
    }
  }
  return contours
}

function edgeIntersectsBbox(edge, bbox) {
  if (edge.maxX < bbox[0]) { return false }
  if (edge.minX > bbox[2]) { return false }
  if (edge.maxY < bbox[1]) { return false }
  if (edge.minY > bbox[3]) { return false }
  return true
}

var IntersectionPoint = function IntersectionPoint(p, edge1, edge2, isHeadingIn) {
  this.p = p;
  this.polylineEdge = edge1;
  this.polygonEdge = edge2;
  this.isHeadingIn = isHeadingIn;

  this.distanceFromPolylineEdgeStart = distance(this.polylineEdge.p1.p, this.p);
  this.distanceFromPolygonEdgeStart = distance(this.polygonEdge.p1.p, this.p);

  this.polygonEdge.intersectionPoints.push(this);
  this.polylineEdge.intersectionPoints.push(this);

  this.visitCount = 0;
};

IntersectionPoint.prototype.incrementVisitCount = function incrementVisitCount () {
  this.visitCount = this.visitCount + 1;
};

function distance(p1, p2) {
  var xs = p2[0] - p1[0];
  var ys = p2[1] - p1[1];
  xs *= xs;
  ys *= ys;

  return Math.sqrt(xs + ys)
}

function findIntersectionPoints(polygonEdges, lineEdges, intersectingPoints) {
  var i, ii, iii;
  var count = lineEdges.length;
  var polyCount = polygonEdges.length;
  for (i = 0; i < count; i++) {
    var lineEdge = lineEdges[i];

    for (ii = 0; ii < polyCount; ii++) {
      var polygonEdge = polygonEdges[ii];
      if (!polygonEdge.intersectPolylineBbox) { continue }

      if (polygonEdge.maxX < lineEdge.minX || polygonEdge.minX > lineEdge.maxX) { continue }
      if (polygonEdge.maxY < lineEdge.minY || polygonEdge.minY > lineEdge.maxY) { continue }
      var intersection = getEdgeIntersection(lineEdge, polygonEdge);
      if (intersection !== null) {
        for (iii = 0; iii < intersection.length; iii++) {
          var isHeadingIn = robustPredicates.orient2d(polygonEdge.p1.p[0], polygonEdge.p1.p[1], polygonEdge.p2.p[0], polygonEdge.p2.p[1], lineEdge.p1.p[0], lineEdge.p1.p[1]);
          var ip = new IntersectionPoint(intersection[iii], lineEdge, polygonEdge, isHeadingIn > 0);
          intersectingPoints.push(ip);
        }
      }
    }
  }
  lineEdges.forEach(function (edge) {
    edge.intersectionPoints.sort(function (a, b) {
      return a.distanceFromPolylineEdgeStart - b.distanceFromPolylineEdgeStart
    });
  });

  polygonEdges.forEach(function (edge) {
    edge.intersectionPoints.sort(function (a, b) {
      return a.distanceFromPolygonEdgeStart - b.distanceFromPolygonEdgeStart
    });
  });
}

var EPSILON = 1e-9;

function crossProduct(a, b) {
  return (a[0] * b[1]) - (a[1] * b[0])
}

function dotProduct(a, b) {
  return (a[0] * b[0]) + (a[1] * b[1])
}

function toPoint(p, s, d) {
  return [
    p[0] + s * d[0],
    p[1] + s * d[1]
  ]
}

function getEdgeIntersection(lineEdge, potentialEdge, noEndpointTouch) {
  var va = [lineEdge.p2.p[0] - lineEdge.p1.p[0], lineEdge.p2.p[1] - lineEdge.p1.p[1]];
  var vb = [potentialEdge.p2.p[0] - potentialEdge.p1.p[0], potentialEdge.p2.p[1] - potentialEdge.p1.p[1]];

  var e = [potentialEdge.p1.p[0] - lineEdge.p1.p[0], potentialEdge.p1.p[1] - lineEdge.p1.p[1]];
  var kross = crossProduct(va, vb);
  var sqrKross = kross * kross;
  var sqrLenA  = dotProduct(va, va);

  if (sqrKross > 0) {

    var s = crossProduct(e, vb) / kross;
    if (s < 0 || s > 1) { return null }
    var t = crossProduct(e, va) / kross;
    if (t < 0 || t > 1) { return null }
    if (s === 0 || s === 1) {
      // on an endpoint of line segment a
      return noEndpointTouch ? null : [toPoint(lineEdge.p1.p, s, va)]
    }
    if (t === 0 || t === 1) {
      // on an endpoint of line segment b
      return noEndpointTouch ? null : [toPoint(potentialEdge.p1.p, t, vb)]
    }
    return [toPoint(lineEdge.p1.p, s, va)]
  }

  var sqrLenE = dotProduct(e, e);
  kross = crossProduct(e, va);
  sqrKross = kross * kross;

  if (sqrKross > EPSILON * sqrLenA * sqrLenE) { return null }

  var sa = dotProduct(va, e) / sqrLenA;
  var sb = sa + dotProduct(va, vb) / sqrLenA;
  var smin = Math.min(sa, sb);
  var smax = Math.max(sa, sb);

  if (smin <= 1 && smax >= 0) {

    if (smin === 1) { return noEndpointTouch ? null : [toPoint(lineEdge.p1.p, smin > 0 ? smin : 0, va)] }

    if (smax === 0) { return noEndpointTouch ? null : [toPoint(lineEdge.p1.p, smax < 1 ? smax : 1, va)] }

    if (noEndpointTouch && smin === 0 && smax === 1) { return null }

    return [
      toPoint(lineEdge.p1.p, smin > 0 ? smin : 0, va),
      toPoint(lineEdge.p1.p, smax < 1 ? smax : 1, va)
    ]
  }

  return null
}

// import { _debugCandidatePoly, _debugIntersectionPoint, _debugLinePoints, _debugIntersectionPoints, _debugPolyStart } from './debug'

function index (polygon, line) {
  var poly = rewind(polygon);

  var intersections = [];
  var polygonEdges = [];
  var polylineEdges = [];
  var polylineBbox = [Infinity, Infinity, Infinity, Infinity];

  var contours = fillQueue(poly, line, polygonEdges, polylineEdges, polylineBbox);

  findIntersectionPoints(polygonEdges, polylineEdges, intersections);

  if (intersections.length === 0) {
    return polygon
  }

  // Track the number of intersections per contour
  // This is useful for holes or outerrings that aren't intersected
  // so that we can manually add them back in at the end
  var numberIntersectionsByRing = {};
  contours.forEach(function (c) { return numberIntersectionsByRing[c.id] = 0; }); //eslint-disable-line
  intersections.forEach(function (i) {
    var id = i.polygonEdge.polygonContourId;
    numberIntersectionsByRing[id] = numberIntersectionsByRing[id] + 1;
  });


  var infiniteLoopGuard = 0;
  var outPolys = [];
  // _debugIntersectionPoints(intersections)
  // Start the rewiring of the outputs from the first intersection point along the polyline line
  // This step makes a difference (eg see the another.geojson harness file)
  var firstPolyStart = null;
  for (var index = 0; index < polylineEdges.length; index++) {
    var pe = polylineEdges[index];
    if (pe.intersectionPoints.length > 0) {
      firstPolyStart = pe.intersectionPoints[0];
      break
    }
  }

  var polyStart = firstPolyStart;
  var nextPolyStart = {visitCount: 1};
  // Basically we're going to walk our way around the outside of the polygon
  // to find new output polygons until we get back to the beginning
  while (firstPolyStart !== nextPolyStart) {
    if (infiniteLoopGuard > intersections.length * 2) {
      break
    }
    infiniteLoopGuard = infiniteLoopGuard++;

    // If we've already visited this intersection point a couple of times we've
    // already used it in it's two output polygons

    if (nextPolyStart.visitCount >= 2) {
      var unvisitedPolyFound = false;
      for (var index$1 = 0; index$1 < intersections.length; index$1++) {
        var intersection = intersections[index$1];
        if (intersection.visitCount < 2) {
          polyStart = intersection;
          unvisitedPolyFound = true;
          break
        }
      }
      if (!unvisitedPolyFound) { break }
    }

    polyStart.visitCount = polyStart.visitCount + 1;
    var outPoly = [];
    outPolys.push(outPoly);
    outPoly.push(polyStart.p);

    polyStart.visitCount = polyStart.visitCount + 1;
    var nextIntersection = walkPolygonForwards(polyStart, outPoly);
    // _debugCandidatePoly(outPolys)
    // After we've walked the first stretch of the polygon we now have the
    // starting point for our next output polygon
    nextPolyStart = nextIntersection;


    // Although sometimes we walk all the way around the outside
    // because our split line goes from outer to inner ring
    var override = false;
    if (nextIntersection === nextPolyStart && intersections.length === 2) {
      for (var index$2 = 0; index$2 < intersections.length; index$2++) {
        var intersection$1 = intersections[index$2];
        if (intersection$1.visitCount < 2) {
          override = true;
        }
      }
    }

    // An ouput polygon has to contain at least 1 stretch from the original polygon
    // and one stretch from the polyline
    // However it can contain many stretches of each
    // So we walk continually from polyline to polygon collecting the output
    while (nextIntersection !== polyStart || override) {
      var methodForPolyline = nextIntersection.isHeadingIn ? walkPolylineForwards : walkPolylineBackwards;
      nextIntersection = methodForPolyline(nextIntersection, outPoly);
      // _debugCandidatePoly(outPolys)

      if (nextIntersection !== polyStart) {
        nextIntersection = walkPolygonForwards(nextIntersection, outPoly);
        // _debugCandidatePoly(outPolys)
      }
      override = false;
    }

    if (nextPolyStart.visitCount >= 2) {
      var unvisitedPolyFound$1 = false;
      for (var index$3 = 0; index$3 < intersections.length; index$3++) {
        var intersection$2 = intersections[index$3];
        if (intersection$2.visitCount < 2) {
          polyStart = intersection$2;
          unvisitedPolyFound$1 = true;
          break
        }
      }
      if (unvisitedPolyFound$1) {
        nextPolyStart = polyStart;
      }
    }

    // Finally we set the next start point based on what we found earlier
    polyStart = nextPolyStart;
  }

  var outCoordinates = outPolys.map(function (poly) { return [poly]; });

  var keys = Object.keys(numberIntersectionsByRing);
  for (var index$4 = 0; index$4 < keys.length; index$4++) {
    var key = keys[index$4];
    var value = numberIntersectionsByRing[key];
    if (value === 0) {
      var edge = findFirstPolygonEdge(polygonEdges, parseInt(key));
      var ring = findRingFromEdge(edge, contours);
      createAsHoleOrAddAsNewOuterRing(ring, outCoordinates);
    }
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'MultiPolygon',
      coordinates: outCoordinates
    }
  }
}

function findFirstPolygonEdge(polygonEdges, contourId) {
  for (var index = 0; index < polygonEdges.length; index++) {
    var edge = polygonEdges[index];
    if (edge.polygonContourId === contourId) { return edge }
  }
}

function findRingFromEdge(edge, contours) {
  var contour = contours.find(function (c) { return c.id === edge.polygonContourId; });
  return contour.rawCoords
}

function createAsHoleOrAddAsNewOuterRing(unusedRing, outCoordinates) {
  for (var index = 0; index < outCoordinates.length; index++) {
    var existingRing = outCoordinates[index];
    if (inside(unusedRing[0], [existingRing[0]])) {
      existingRing.push(unusedRing);
      return
    }
  }
  // If no match is found push it as a new outer ring
  outCoordinates.push([unusedRing]);
}

// Walk around the polygon collecting vertices
function walkPolygonForwards(intersectionPoint, outPoly) {
  var nextEdge = intersectionPoint.polygonEdge;
  if (nextEdge.intersectionPoints.length > 1) {
    // _debugIntersectionPoint(intersectionPoint)
    var lastPointOnEdge = nextEdge.intersectionPoints[nextEdge.intersectionPoints.length - 1];
    if (lastPointOnEdge !== intersectionPoint) {
      var currentIndex = findIndexOfIntersectionPoint(intersectionPoint, nextEdge.intersectionPoints);
      var nextIp = nextEdge.intersectionPoints[currentIndex + 1];
      outPoly.push(nextIp.p);
      nextIp.incrementVisitCount();
      return nextIp
    }
  }
  var condition = true;
  while (condition) {
    outPoly.push(nextEdge.p2.p);
    nextEdge = nextEdge.nextEdge;
    if (nextEdge === null) { return intersectionPoint }
    else if (nextEdge.intersectionPoints.length > 0) { condition = false; }
  }
  nextEdge.intersectionPoints[0].incrementVisitCount();
  outPoly.push(nextEdge.intersectionPoints[0].p);
  return nextEdge.intersectionPoints[0]
}

// Given a set of intersections find the next one
function findIndexOfIntersectionPoint(intersection, intersections) {
  for (var index = 0; index < intersections.length; index++) {
    var int = intersections[index];
    if (int === intersection) { return index }
  }
  return null
}


function walkPolylineBackwards(intersectionPoint, outPoly) {
  var nextEdge = intersectionPoint.polylineEdge;
  if (nextEdge.intersectionPoints.length === 2) {
    var lastPointOnEdge = nextEdge.intersectionPoints[nextEdge.intersectionPoints.length - 1];
    // debugger
    if (lastPointOnEdge === intersectionPoint) {
      var nextIntersection = nextEdge.intersectionPoints[0];
      outPoly.push(nextIntersection.p);
      nextIntersection.incrementVisitCount();
      return nextIntersection
    } else {
      outPoly.push(lastPointOnEdge.p);
      lastPointOnEdge.incrementVisitCount();
      return lastPointOnEdge
    }
  } else if (nextEdge.intersectionPoints.length > 2) {
    // _debugIntersectionPoint(intersectionPoint)

    var lastPointOnEdge$1 = nextEdge.intersectionPoints[0];
    if (lastPointOnEdge$1 !== intersectionPoint) {
      var currentIndex = findIndexOfIntersectionPoint(intersectionPoint, nextEdge.intersectionPoints);
      var nextIntersection$1 = nextEdge.intersectionPoints[currentIndex - 1];
      outPoly.push(nextIntersection$1.p);
      nextIntersection$1.incrementVisitCount();
      return nextIntersection$1
    }
  }
  var condition = true;
  while (condition) {
    outPoly.push(nextEdge.p1.p);
    nextEdge = nextEdge.prevEdge;
    if (nextEdge.originalIndex === undefined) { return intersectionPoint }
    else if (nextEdge.intersectionPoints.length > 0) {
      condition = false;
    }
  }
  if (nextEdge.originalIndex === undefined) { return intersectionPoint }
  var lastIntersection = nextEdge.intersectionPoints[nextEdge.intersectionPoints.length - 1];
  lastIntersection.incrementVisitCount();
  outPoly.push(lastIntersection.p);
  return lastIntersection
}

function walkPolylineForwards(intersectionPoint, outPoly) {
  var nextEdge = intersectionPoint.polylineEdge;

  if (nextEdge.intersectionPoints.length > 1) {
    // _debugIntersectionPoint(intersectionPoint)
    var lastPointOnEdge = nextEdge.intersectionPoints[nextEdge.intersectionPoints.length - 1];
    if (lastPointOnEdge !== intersectionPoint) {
      var currentIndex = findIndexOfIntersectionPoint(intersectionPoint, nextEdge.intersectionPoints);
      var nextIp = nextEdge.intersectionPoints[currentIndex + 1];
      outPoly.push(nextIp.p);
      nextIp.incrementVisitCount();
      return nextIp
    }
  }
  var condition = true;
  while (condition) {
    outPoly.push(nextEdge.p2.p);
    nextEdge = nextEdge.nextEdge;
    if (nextEdge === null) { return intersectionPoint }
    else if (nextEdge.intersectionPoints.length > 0) { condition = false; }
  }
  if (nextEdge === undefined) { return intersectionPoint }
  var lastIntersection = nextEdge.intersectionPoints[0];
  lastIntersection.incrementVisitCount();
  outPoly.push(lastIntersection.p);
  return lastIntersection
}

export { index as default };
